import { writable, type Readable, type Stores, type Writable } from "svelte/store";
import { Soundboard, type Scene, type SceneKind } from "./Soundboard";
import { PlaySettings, type SoundCard } from "./SoundCard";
import { MOBILE_SCREEN_WIDTH, readDarkThemeFromLocalStorage, showToastWrapper } from "./global";
import { LibrarySearchIndex, WELL_KNOWN_LIBRARY_QUERIES } from "./library/search/LibrarySearch";
import { UserInfo } from "./lib/UserInfo";
import { UserSettingsBankDescription, type UserSettings } from "./lib/UserSettings";
import { SettingsBank, SettingValue, type SettingId } from "./lib/Settings";

export const nowPlaying = writable([]);

export function lateInit<T>() {
    const { set, update, subscribe } = writable([])

    function init(elem: T[]) {
        set(elem);
    }

    function wrappedUpdate(mutator) {
        update(mutator)
    }
    return {
        init,
        update: wrappedUpdate,
        subscribe,
    }
}
export const librarySounds: Readable<any[]> = lateInit<any[]>();
export const librarySoundSelected = writable(null);
export const libraryScenes = writable(new Map());
export const librarySceneSelected = writable(null);
export const libraryTabSelected = writable(0);
export const existingCardSelected = writable<SoundCard>(null);
export const isDarkTheme = writable(readDarkThemeFromLocalStorage());

function createLibrarySearchIndex() {
    let index = LibrarySearchIndex.empty();
    const { subscribe, set, update } = writable(index);
    return {
        subscribe,
        update: update,
        UpdateInPlace: (updater: (index: LibrarySearchIndex) => void) => {
            updater(index);
            set(index);
        }
    }
}
export const librarySearchIndex = createLibrarySearchIndex();
export const librarySearchQuery = writable(WELL_KNOWN_LIBRARY_QUERIES.All());

export const windowWidth = writable(0);
export const windowHeight = writable(0);

function createSoundboardStore() {
    let sb = new Soundboard({ lanes: [], lane_coord_map: {}, card_coord_map: {} });
    const { subscribe, set, update } = writable(sb);

    const updateError = "Error updating board. Please refresh and try again, or contact support@bardic.tools.";
    const updateInBackend = async (updater: (sb: Soundboard) => Soundboard) => {
        let updated = null;
        let previous = null;
        update((sb) => {
            previous = structuredClone(sb);
            try {
                updated = updater(sb);
                return updated;
            } catch (error) {
                showToastWrapper(updateError, true);
                console.error("Error updating the board: ", error);
                return sb;
            }
        });
        // @ts-ignore
        try {
            const res = await updated.store()
            if (res instanceof Error) {
                showToastWrapper(res, true);
                console.error("Error store the updated board: ", res, "board was", updated);
                set(new Soundboard(previous))
            }
        } catch (error) {
            showToastWrapper(updateError, true);
            console.error("Error store the updated board: ", error, "board was", updated);
            set(new Soundboard(previous))
        }
    }
    return {
        subscribe,
        update: updateInBackend,
        replace: (rawData) => {
            updateInBackend((sb) => {
                const newSb = new Soundboard(rawData);
                return newSb;
            });
        },
        _board: sb,
        Update: updateInBackend,
        UpdateVoid: (callback: () => void) => updateInBackend((sb) => { callback(); return sb }),
        // TODO BL: For now, these mutations have to be accessed from the raw store object (soundboard instead of $soundboard).
        // $soundBoard gives us reactive access to the underlying Soundboard, and that one doesn't auto-update.
        // I should make this better in the future.
        DeleteCard: (sceneId: string, cardId: string) => updateInBackend((sb) => {
            sb.DeleteCard(sceneId, cardId); return sb;
        }),
        AddCardToLane: (laneId, title, sound) => updateInBackend((sb) => {
            sb.AddCardToLane(laneId, title, sound); return sb;
        }),
        AddScene: (kind: SceneKind) => updateInBackend((sb) => {
            sb.AddScene(kind); return sb;
        }),
        AddExistingRawScene: (rawScene: any, atIdx: number) => updateInBackend((sb) => {
            sb.AddExistingRawScene(rawScene, atIdx); return sb;
        }),
        RenameCard: (sceneId, cardId, newName) => updateInBackend((sb) => {
            sb.RenameCard(sceneId, cardId, newName); return sb;
        }),
        ChangeCardPlaySettings: (sceneId, cardId, mutator: (PlaySettings) => PlaySettings) => updateInBackend((sb) => {
            sb.ChangeCardPlaySettings(sceneId, cardId, mutator); return sb;
        }),
        RenameScene: (sceneId: string, newName: string) => updateInBackend((sb) => {
            sb.RenameScene(sceneId, newName); return sb;
        }),
        DeleteScene: (sceneId: string) => updateInBackend((sb) => {
            sb.DeleteScene(sceneId); return sb;
        }),
        MoveScene: (oldIdx, newIdx) => updateInBackend((sb) => {
            sb.MoveScene(oldIdx, newIdx); return sb;
        }),
    }
}

export const soundBoard = createSoundboardStore();

function createUserInfoStore() {
    const userInfo = writable(UserInfo.empty())
    fetch("/auth/user_info")
        .then((resp) => resp.json())
        .then((data) => {
            userInfo.set(UserInfo.fromRaw(data))
        })
    return userInfo;
}
export const userInfo = createUserInfoStore();


export enum ScreenLayouts {
    NotMobile = "NotMobile",
    Mobile = "Mobile",
}
export const screenLayout = writable(ScreenLayouts.NotMobile);

windowWidth.subscribe((width) => {
    if (width <= MOBILE_SCREEN_WIDTH) {
        screenLayout.set(ScreenLayouts.Mobile);
    } else {
        screenLayout.set(ScreenLayouts.NotMobile);
    }
});

function createUserSettingsStore() {
    const userSettings = writable(SettingsBank.default<UserSettings>(UserSettingsBankDescription))
    fetch("/app/profile/user_settings/json")
        .then((resp) => resp.json())
        .then((data) => {
            const rawSettings = JSON.parse(data)
            const wrappedSettings: {[index: SettingId]: SettingValue<any>} = {}
            for (const [settingId, value] of Object.entries(rawSettings)) {
                wrappedSettings[settingId] = new SettingValue<boolean>({
                    id: settingId,
                    value: value as boolean,
                }, UserSettingsBankDescription[settingId])
            }
            const newSettings = new SettingsBank(wrappedSettings, UserSettingsBankDescription)
            console.debug("BL: Loaded user settings", newSettings);
            userSettings.set(newSettings);
        })
    return userSettings;
}
export const userSettings: Writable<SettingsBank<UserSettings>> = createUserSettingsStore();